import { ReactNode, CSSProperties, MouseEvent } from "react";
import styles from "./Box.module.css";

export interface BoxProps {
  children?: ReactNode;
  variant?: "button";
  padding?: number | string;
  paddingHorizontal?: number | string;
  paddingVertical?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  margin?: number | string;
  marginHorizontal?: number | string;
  marginVertical?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  backgroundColor?: string;
  border?: number | string;
  borderTop?: number | string;
  borderBottom?: number | string;
  borderLeft?: number | string;
  borderRight?: number | string;
  borderColor?: string;
  borderRadius?: number | string;
  overflow?: CSSProperties["overflow"];
  overflowX?: CSSProperties["overflowX"];
  overflowY?: CSSProperties["overflowY"];
  scrollable?: boolean;
  flexGrow?: number;
  flexShrink?: number;
  className?: string;
  style?: CSSProperties;
  onPress?: () => void;
  onClick?: (e?: MouseEvent) => void;
  onMouseEnter?: () => void;
}

export default function Box({
  children,
  variant,
  padding,
  paddingHorizontal,
  paddingVertical,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  margin,
  marginHorizontal,
  marginVertical,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  backgroundColor,
  border,
  borderTop,
  borderBottom,
  borderLeft,
  borderRight,
  borderColor,
  borderRadius,
  overflow,
  overflowX,
  overflowY,
  scrollable,
  flexGrow,
  flexShrink,
  className = "",
  style,
  onPress,
  onClick,
  onMouseEnter,
}: BoxProps) {
  const inlineStyle: CSSProperties = {
    ...(padding !== undefined && {
      padding:
        typeof padding === "number" ? `var(--spacing-${padding})` : padding,
    }),
    ...(paddingHorizontal !== undefined && {
      paddingLeft:
        typeof paddingHorizontal === "number"
          ? `var(--spacing-${paddingHorizontal})`
          : paddingHorizontal,
      paddingRight:
        typeof paddingHorizontal === "number"
          ? `var(--spacing-${paddingHorizontal})`
          : paddingHorizontal,
    }),
    ...(paddingVertical !== undefined && {
      paddingTop:
        typeof paddingVertical === "number"
          ? `var(--spacing-${paddingVertical})`
          : paddingVertical,
      paddingBottom:
        typeof paddingVertical === "number"
          ? `var(--spacing-${paddingVertical})`
          : paddingVertical,
    }),
    ...(paddingTop !== undefined && {
      paddingTop:
        typeof paddingTop === "number"
          ? `var(--spacing-${paddingTop})`
          : paddingTop,
    }),
    ...(paddingBottom !== undefined && {
      paddingBottom:
        typeof paddingBottom === "number"
          ? `var(--spacing-${paddingBottom})`
          : paddingBottom,
    }),
    ...(paddingLeft !== undefined && {
      paddingLeft:
        typeof paddingLeft === "number"
          ? `var(--spacing-${paddingLeft})`
          : paddingLeft,
    }),
    ...(paddingRight !== undefined && {
      paddingRight:
        typeof paddingRight === "number"
          ? `var(--spacing-${paddingRight})`
          : paddingRight,
    }),
    ...(margin !== undefined && {
      margin: typeof margin === "number" ? `var(--spacing-${margin})` : margin,
    }),
    ...(marginHorizontal !== undefined && {
      marginLeft:
        typeof marginHorizontal === "number"
          ? `var(--spacing-${marginHorizontal})`
          : marginHorizontal,
      marginRight:
        typeof marginHorizontal === "number"
          ? `var(--spacing-${marginHorizontal})`
          : marginHorizontal,
    }),
    ...(marginVertical !== undefined && {
      marginTop:
        typeof marginVertical === "number"
          ? `var(--spacing-${marginVertical})`
          : marginVertical,
      marginBottom:
        typeof marginVertical === "number"
          ? `var(--spacing-${marginVertical})`
          : marginVertical,
    }),
    ...(marginTop !== undefined && {
      marginTop:
        typeof marginTop === "number"
          ? `var(--spacing-${marginTop})`
          : marginTop,
    }),
    ...(marginBottom !== undefined && {
      marginBottom:
        typeof marginBottom === "number"
          ? `var(--spacing-${marginBottom})`
          : marginBottom,
    }),
    ...(marginLeft !== undefined && {
      marginLeft:
        typeof marginLeft === "number"
          ? `var(--spacing-${marginLeft})`
          : marginLeft,
    }),
    ...(marginRight !== undefined && {
      marginRight:
        typeof marginRight === "number"
          ? `var(--spacing-${marginRight})`
          : marginRight,
    }),
    ...(width !== undefined && {
      width: typeof width === "number" ? `var(--spacing-${width})` : width,
    }),
    ...(height !== undefined && {
      height: typeof height === "number" ? `var(--spacing-${height})` : height,
    }),
    ...(minWidth !== undefined && {
      minWidth:
        typeof minWidth === "number" ? `var(--spacing-${minWidth})` : minWidth,
    }),
    ...(minHeight !== undefined && {
      minHeight:
        typeof minHeight === "number"
          ? `var(--spacing-${minHeight})`
          : minHeight,
    }),
    ...(maxWidth !== undefined && {
      maxWidth:
        typeof maxWidth === "number" ? `var(--spacing-${maxWidth})` : maxWidth,
    }),
    ...(maxHeight !== undefined && {
      maxHeight:
        typeof maxHeight === "number"
          ? `var(--spacing-${maxHeight})`
          : maxHeight,
    }),
    ...(backgroundColor !== undefined && { backgroundColor }),
    ...(borderColor !== undefined && { borderColor }),
    ...(border !== undefined && {
      border:
        typeof border === "number"
          ? `${border}px solid ${borderColor || "currentColor"}`
          : border,
    }),
    ...(borderTop !== undefined && {
      borderTop:
        typeof borderTop === "number"
          ? `${borderTop}px solid ${borderColor || "currentColor"}`
          : borderTop,
    }),
    ...(borderBottom !== undefined && {
      borderBottom:
        typeof borderBottom === "number"
          ? `${borderBottom}px solid ${borderColor || "currentColor"}`
          : borderBottom,
    }),
    ...(borderLeft !== undefined && {
      borderLeft:
        typeof borderLeft === "number"
          ? `${borderLeft}px solid ${borderColor || "currentColor"}`
          : borderLeft,
    }),
    ...(borderRight !== undefined && {
      borderRight:
        typeof borderRight === "number"
          ? `${borderRight}px solid ${borderColor || "currentColor"}`
          : borderRight,
    }),
    ...(borderRadius !== undefined && {
      borderRadius:
        typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
    }),
    ...(overflow !== undefined && { overflow }),
    ...(overflowX !== undefined && { overflowX }),
    ...(overflowY !== undefined && { overflowY }),
    ...(scrollable && { overflowY: "auto" as const }),
    ...(flexGrow !== undefined && { flexGrow }),
    ...(flexShrink !== undefined && { flexShrink }),
    ...style,
  };

  const handleClick = onClick || onPress;

  // Hide scrollbar by default when overflow is set or scrollable is true
  const hasOverflow =
    overflow !== undefined ||
    overflowX !== undefined ||
    overflowY !== undefined ||
    scrollable;
  const combinedClassName = hasOverflow
    ? `${styles.hideScrollbar} ${className}`.trim()
    : className;

  if (variant === "button") {
    return (
      <button
        type="button"
        className={combinedClassName}
        style={inlineStyle}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
      >
        {children}
      </button>
    );
  }

  if (handleClick) {
    return (
      <div
        className={combinedClassName}
        style={inlineStyle}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={combinedClassName}
      style={inlineStyle}
      onMouseEnter={onMouseEnter}
    >
      {children}
    </div>
  );
}
